import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useOverview } from "@/lib/useOverview";
import DateRange from "@/components/DateRange";
import SocialPerformance from "@/components/dashboard/SocialPerformance";
import WhatsWorking from "@/components/dashboard/WhatsWorking";
import ContentCard from "@/components/ContentCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const SORTS = [
  { value: "engagement", label: "Best performing" },
  { value: "reach", label: "Reach" },
  { value: "impressions", label: "Impressions" },
  { value: "engagement_rate", label: "Engagement rate" },
  { value: "latest", label: "Latest" },
];
const PLATFORMS = [
  { value: "all", label: "All platforms" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
];

export default function Social() {
  const [range, setRange] = useState(30);
  const [sort, setSort] = useState("engagement");
  const [platform, setPlatform] = useState("all");
  const { data: overview, isLoading } = useOverview(range);
  const { data: content } = useQuery({
    queryKey: ["content", sort, platform],
    queryFn: async () => (await api.get(`/dashboard/content?sort=${sort}&platform=${platform}`)).data,
  });

  if (isLoading || !overview) {
    return <div className="grid h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Social</h1>
          <p className="mt-1 text-muted-foreground">Your channels and the content that moved them.</p>
        </div>
        <DateRange value={range} onChange={setRange} />
      </header>

      <SocialPerformance social={overview.social} />
      <WhatsWorking posts={overview.whats_working} />

      <section data-testid="social-content">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Content Performance</h2>
            <p className="text-sm text-muted-foreground">Every creative, ranked by what it achieved.</p>
          </div>
          <div className="flex gap-3">
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-[150px] rounded-full" data-testid="content-platform-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[170px] rounded-full" data-testid="content-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="content-grid">
          {content?.map((p) => <ContentCard key={p.post_id} post={p} />)}
        </div>
      </section>
    </div>
  );
}
